/**
 * Popup shown when a nurse taps "Commit & Send Request" but one or more
 * compliance items would not be valid on the visit date. The server
 * returns a 409 with a structured `{ blockers, asOfDate }` payload; we
 * render each blocker with a human-readable reason so the nurse knows
 * exactly what to renew before they can take the visit.
 *
 * OR-group blockers (Flu vaccine OR Flu declination) are visually grouped
 * with an "Any of these is needed" subheader so the nurse understands
 * she only needs ONE current member, not all of them.
 */
import { LinearGradient } from "expo-linear-gradient";
import { useRouter } from "expo-router";
import { Modal, Pressable, ScrollView, StyleSheet, TouchableOpacity, View } from "react-native";
import { Icon, Text } from "react-native-paper";

import type { VisitComplianceBlocker, VisitComplianceBlocked } from "@/lib/api";
import { colors, fonts, gradients, radius, shadows, spacing, typography } from "@/lib/theme";

interface Props {
    visible: boolean;
    blocked: VisitComplianceBlocked | null;
    onClose: () => void;
}

/** Compact `YYYY-MM-DD` → `Mon DD, YYYY` for the message text. */
function fmtDate(iso: string): string {
    // Force a Z so the date doesn't shift one day in the user's timezone —
    // these are calendar dates, not instants.
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC"
    });
}

function reasonLine(b: VisitComplianceBlocker, asOfDate: string): string {
    // A training item is renewed by taking the course, so "uploaded" /
    // "marked" wording would send her looking for a document that doesn't exist.
    if (b.trainingBundleSlug) {
        return b.reason === "expires_before_visit" && b.expiryDate
            ? `Due ${fmtDate(b.expiryDate)}, finish the training to take this visit`
            : "Expired, finish the training to take visits again";
    }
    switch (b.reason) {
        case "expires_before_visit":
            return b.expiryDate
                ? `Expires ${fmtDate(b.expiryDate)} — visit is ${fmtDate(asOfDate)}`
                : `Will be expired by the visit date`;
        case "not_effective_yet":
            return b.effectiveDate
                ? `Not effective until ${fmtDate(b.effectiveDate)} — visit is ${fmtDate(asOfDate)}`
                : `Not yet effective on the visit date`;
        case "missing":
            return "Not uploaded yet";
        case "not_compliant":
            return "Marked as not compliant";
        case "pending_review":
            return "Still under review";
    }
}

export function VisitComplianceBlockerModal({ visible, blocked, onClose }: Props) {
    const router = useRouter();
    if (!blocked) {
        return null;
    }

    const startTraining = (slug: string) => {
        onClose();
        router.push(`/training/${slug}`);
    };

    // Partition blockers: standalone first, then OR-groups (collapsed by
    // orGroupId). The mobile UI lists each standalone item as its own row;
    // each group as a labelled cluster of alternatives.
    const standalone = blocked.blockers.filter((b) => b.orGroupId == null);
    const groups = new Map<number, VisitComplianceBlocker[]>();
    for (const b of blocked.blockers) {
        if (b.orGroupId == null) {
            continue;
        }
        const list = groups.get(b.orGroupId) ?? [];
        list.push(b);
        groups.set(b.orGroupId, list);
    }

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <Pressable style={styles.overlay} onPress={onClose}>
                <Pressable style={styles.card} onPress={(e) => e.stopPropagation()}>
                    <View style={styles.headerRow}>
                        <View style={styles.iconCircle}>
                            <Icon source="alert-octagon-outline" size={26} color={colors.error} />
                        </View>
                        <Text style={styles.title}>Can't request this visit yet</Text>
                    </View>
                    <Text style={styles.subhead}>
                        Some of your compliance items won't be valid on the visit date (
                        {fmtDate(blocked.asOfDate)}). Renew them and try again.
                    </Text>

                    <ScrollView
                        style={styles.list}
                        contentContainerStyle={{ gap: spacing.sm }}
                        showsVerticalScrollIndicator={false}
                    >
                        {standalone.map((b) => (
                            <BlockerRow
                                key={b.itemId}
                                blocker={b}
                                asOfDate={blocked.asOfDate}
                                onStartTraining={startTraining}
                            />
                        ))}
                        {Array.from(groups.entries()).map(([gid, members]) => (
                            <View key={`group-${gid}`} style={styles.groupBox}>
                                <Text style={styles.groupLabel}>Any of these is needed:</Text>
                                {members.map((b) => (
                                    <BlockerRow
                                        key={b.itemId}
                                        blocker={b}
                                        asOfDate={blocked.asOfDate}
                                        onStartTraining={startTraining}
                                    />
                                ))}
                            </View>
                        ))}
                    </ScrollView>

                    <TouchableOpacity onPress={onClose} activeOpacity={0.85}>
                        <LinearGradient
                            colors={[...gradients.brand]}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={[styles.cta, shadows.md]}
                        >
                            <Text style={styles.ctaText}>Got it</Text>
                        </LinearGradient>
                    </TouchableOpacity>
                </Pressable>
            </Pressable>
        </Modal>
    );
}

function BlockerRow({
    blocker,
    asOfDate,
    onStartTraining
}: {
    blocker: VisitComplianceBlocker;
    asOfDate: string;
    onStartTraining: (slug: string) => void;
}) {
    const slug = blocker.trainingBundleSlug;
    return (
        <View style={styles.row}>
            <Icon
                source={slug ? "school-outline" : "file-document-outline"}
                size={20}
                color={colors.textSecondary}
            />
            <View style={styles.rowText}>
                <Text style={styles.rowName}>{blocker.itemName}</Text>
                <Text style={styles.rowReason}>{reasonLine(blocker, asOfDate)}</Text>
                {slug ? (
                    <TouchableOpacity
                        onPress={() => onStartTraining(slug)}
                        activeOpacity={0.85}
                        style={styles.trainingBtn}
                    >
                        <Text style={styles.trainingBtnText}>Start training</Text>
                    </TouchableOpacity>
                ) : null}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    overlay: {
        flex: 1,
        backgroundColor: "rgba(0,0,0,0.55)",
        justifyContent: "center",
        alignItems: "center",
        paddingHorizontal: spacing.lg
    },
    card: {
        width: "100%",
        maxWidth: 420,
        backgroundColor: colors.surface,
        borderRadius: radius.xl,
        padding: spacing.lg,
        gap: spacing.md,
        maxHeight: "80%"
    },
    headerRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm
    },
    iconCircle: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: colors.errorLight,
        alignItems: "center",
        justifyContent: "center"
    },
    title: {
        ...typography.title,
        color: colors.textPrimary,
        flex: 1
    },
    subhead: {
        ...typography.body,
        color: colors.textSecondary
    },
    list: {
        maxHeight: 320
    },
    row: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.sm,
        padding: spacing.md,
        borderRadius: radius.md,
        backgroundColor: colors.backgroundSecondary
    },
    rowText: { flex: 1 },
    rowName: {
        ...typography.subtitle,
        color: colors.textPrimary
    },
    rowReason: {
        ...typography.bodySmall,
        color: colors.error,
        marginTop: 2
    },
    groupBox: {
        gap: spacing.xs,
        padding: spacing.sm,
        borderRadius: radius.md,
        borderWidth: 1,
        borderColor: colors.borderStrong,
        borderStyle: "dashed"
    },
    groupLabel: {
        ...typography.caption,
        color: colors.textSecondary,
        marginBottom: 4,
        fontFamily: fonts.bold,
        textTransform: "uppercase",
        letterSpacing: 0.5
    },
    cta: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: radius.full,
        paddingVertical: 14
    },
    ctaText: {
        ...typography.subtitle,
        color: colors.surface
    },
    trainingBtn: {
        alignSelf: "flex-start",
        marginTop: spacing.sm,
        paddingVertical: 8,
        paddingHorizontal: spacing.md,
        borderRadius: radius.full,
        backgroundColor: colors.teal400
    },
    trainingBtnText: {
        ...typography.bodySmall,
        fontFamily: fonts.bold,
        color: colors.surface
    }
});
